/** Lists home (Slice 8) — the organized list index that replaces the flat CrmListsBar tiles:
 * search + sort (recently viewed / name / most entries) + type-filter chips (All / Opportunities /
 * People / Organizations), rows with a type icon · name · kind badge · entry count · ⋯ (Rename /
 * Delete, native lists only). Ordering/filtering is the pure crmListsIndex helpers. */
import { useMemo, useState } from 'react';
import styles from './crm.module.css';
import { useCrmLists, useDeleteList, useUpdateList } from '../../hooks/useCrm';
import { CrmListWizard } from './CrmListWizard';
import { CrmPopover } from './CrmPopover';
import { CrmRenameModal } from './CrmRenameModal';
import {
  listTypeOf, readRecentLists, sortAndFilterLists,
  type ListSort, type ListType, type ListTypeFilter,
} from './crmListsIndex';

const TYPE_GLYPH: Record<ListType, string> = { opportunity: '$', person: '◔', organization: '▣' };
const TYPE_CHIPS: { key: ListTypeFilter; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'opportunity', label: 'Opportunities' },
  { key: 'person', label: 'People' },
  { key: 'organization', label: 'Organizations' },
];

export function CrmListsHome({ openList }: { openList: (id: number) => void }) {
  const lists = useCrmLists();
  const deleteList = useDeleteList();
  const updateList = useUpdateList();
  const [wizardOpen, setWizardOpen] = useState(false);
  const [menu, setMenu] = useState<{ id: number; name: string; anchorEl: HTMLElement } | null>(null);
  const [renaming, setRenaming] = useState<{ id: number; name: string } | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState<ListSort>('recent');
  const [typeFilter, setTypeFilter] = useState<ListTypeFilter>('all');
  // mount-time snapshot of the recently-viewed ring — the home unmounts when a list opens, so it's
  // re-read fresh on return (no need to react to storage changes while mounted).
  const recentIds = useMemo(() => readRecentLists(), []);

  const rows = lists.data?.lists ?? [];
  const shown = useMemo(
    () => sortAndFilterLists(rows, { search, sort, typeFilter, recentIds }),
    [rows, search, sort, typeFilter, recentIds],
  );

  const confirmDelete = (id: number, name: string) => {
    if (!window.confirm(`Delete "${name}"? Its rows and columns are removed. This can't be undone.`)) return;
    setErr(null);
    deleteList.mutate(id, { onError: () => setErr(`Could not delete "${name}" — please try again.`) });
  };
  const doRename = (name: string) => {
    if (!renaming) return;
    setErr(null);
    updateList.mutate({ listId: renaming.id, name }, {
      onSuccess: () => setRenaming(null),
      onError: () => setErr('Could not rename the list — please try again.'),
    });
  };

  return (
    <>
      {wizardOpen && (
        <CrmListWizard onClose={() => setWizardOpen(false)} onCreated={(id) => { setWizardOpen(false); openList(id); }} />
      )}
      <div className={styles.groupLabel}>
        Lists · {rows.length}{' '}
        <span className={styles.groupHint}>— native, running parallel to Affinity</span>
        <button className={styles.newListBtn} onClick={() => setWizardOpen(true)}>+ New list</button>
      </div>

      <div className={styles.listsControls}>
        <input
          className={styles.gridSearch}
          type="search"
          placeholder="Search lists…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <select className={styles.gridGroupBy} value={sort} onChange={(e) => setSort(e.target.value as ListSort)} aria-label="Sort lists">
          <option value="recent">Recently viewed</option>
          <option value="name">Name A–Z</option>
          <option value="entries">Most entries</option>
        </select>
        <span className={styles.typeChips}>
          {TYPE_CHIPS.map((c) => (
            <button
              key={c.key}
              className={`${styles.typeChip} ${typeFilter === c.key ? styles.typeChipActive : ''}`}
              onClick={() => setTypeFilter(c.key)}
            >{c.label}</button>
          ))}
        </span>
      </div>

      {/* page banner is for delete errors; a rename error surfaces INSIDE the modal (a page-flow
          banner would render behind the modal backdrop — adv-review). Suppress it while renaming. */}
      {err && !renaming && <div className={styles.error} role="alert" onClick={() => setErr(null)}>{err}</div>}

      <div className={styles.listRows}>
        {shown.map((l) => {
          const t = listTypeOf(l);
          return (
            <div key={l.id} className={styles.listRow}>
              <button className={styles.listRowMain} onClick={() => openList(l.id)}>
                <span className={styles.listRowType} data-type={t} title={t}>{TYPE_GLYPH[t]}</span>
                <span className={styles.listRowName}>{l.name}</span>
                <span className={styles.listRowKind}>{l.list_kind}</span>
                {/* D8: whole-record proposals awaiting Accept/Dismiss — a separate badge so the
                    committed "N entries" count stays honest (§7 decision 1). */}
                {(l.proposed_count ?? 0) > 0 && (
                  <span className={styles.proposedBadge} title="AI-proposed records awaiting review">+{l.proposed_count} proposed</span>
                )}
                <span className={styles.listRowCount}>{l.entry_count} {l.entry_count === 1 ? 'entry' : 'entries'}</span>
              </button>
              {l.list_kind === 'native' && (
                <button
                  className={styles.listRowMenuBtn}
                  aria-label={`Actions for ${l.name}`}
                  onClick={(e) => { e.stopPropagation(); setMenu({ id: l.id, name: l.name, anchorEl: e.currentTarget }); }}
                >⋯</button>
              )}
            </div>
          );
        })}
        {rows.length === 0 && <span className={styles.gridEmpty}>No lists yet — create one, or run the IOT import.</span>}
        {rows.length > 0 && shown.length === 0 && <span className={styles.gridEmpty}>No lists match your search / filter.</span>}
      </div>

      {menu && (
        <CrmPopover anchorEl={menu.anchorEl} align="right" onClose={() => setMenu(null)}>
          <button
            role="menuitem"
            className={styles.popoverItem}
            onClick={() => { const m = menu; setMenu(null); setErr(null); setRenaming({ id: m.id, name: m.name }); }}
          >Rename</button>
          <button
            role="menuitem"
            className={`${styles.popoverItem} ${styles.popoverDanger}`}
            onClick={() => { const m = menu; setMenu(null); confirmDelete(m.id, m.name); }}
          >Delete list</button>
        </CrmPopover>
      )}

      {renaming && (
        <CrmRenameModal
          title="Rename list"
          initialName={renaming.name}
          saving={updateList.isPending}
          error={err}
          onSave={doRename}
          onClose={() => { setRenaming(null); setErr(null); }}
        />
      )}
    </>
  );
}
